// ===========================================
// Accept Invitation Page
// ===========================================
// Allows secondary contacts to accept an invitation and create their account

import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
  TextField,
  LinearProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  useValidateInvitationToken,
  useAcceptInvitation,
} from '../../hooks/useInvitations';
import PublicFooter from '../../components/public/PublicFooter';
import { InvitationType } from '../../api/invitations.api';
import { usePublicLegal } from '../../hooks/usePublicLegal';

// ===========================================
// PASSWORD VALIDATION
// ===========================================

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

const validatePassword = (password: string): PasswordValidation => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

const getPasswordStrength = (validation: PasswordValidation): number => {
  const checks = Object.values(validation);
  const passed = checks.filter(Boolean).length;
  return (passed / checks.length) * 100;
};

const getStrengthColor = (strength: number): 'error' | 'warning' | 'success' => {
  if (strength < 40) return 'error';
  if (strength < 80) return 'warning';
  return 'success';
};

// ===========================================
// COMPONENT
// ===========================================

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // #442 D1c: a second contact agrees to the T&Cs + Privacy as they create their account.
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);

  // Get invitation type from URL (for student invitations)
  const invitationType = useMemo<InvitationType | undefined>(() => {
    const type = searchParams.get('type');
    if (type === 'student') return 'STUDENT';
    if (type === 'parent') return 'PARENT';
    return undefined;
  }, [searchParams]);

  // Validate token on page load
  const { data: validationResult, isLoading: isValidating, error: validationError } =
    useValidateInvitationToken(token, invitationType);

  const acceptInvitation = useAcceptInvitation();

  // Password validation
  const passwordValidation = validatePassword(password);
  const passwordStrength = getPasswordStrength(passwordValidation);
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Get invitation type from response (prefer response over URL param)
  const effectiveType = useMemo<InvitationType | undefined>(() => {
    if (validationResult && 'invitationType' in validationResult) {
      return validationResult.invitationType as InvitationType;
    }
    return invitationType;
  }, [validationResult, invitationType]);

  const isStudentInvitation = effectiveType === 'STUDENT';
  // Parent invitations only (the page is shared with student invitations). The server
  // refuses a parent account without both ticks.
  const needsAgreement = !isStudentInvitation;
  const agreed = !needsAgreement || (termsChecked && privacyChecked);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !isPasswordValid || !passwordsMatch || !agreed) return;

    setSubmitted(true);

    try {
      await acceptInvitation.mutateAsync({
        token,
        password,
        type: effectiveType,
        agreement: needsAgreement
          ? { termsAccepted: termsChecked, privacyAccepted: privacyChecked }
          : undefined,
      });
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  // Check if token is invalid
  const isTokenInvalid = validationError || (validationResult && 'valid' in validationResult && !validationResult.valid);
  const tokenError = validationResult && 'error' in validationResult ? validationResult.error : null;

  // Get error message based on token error
  const getTokenErrorMessage = () => {
    const contactText = isStudentInvitation ? 'your music school' : 'the primary contact';
    switch (tokenError) {
      case 'EXPIRED':
        return `This invitation has expired. Please ask ${contactText} to send a new invitation.`;
      case 'ALREADY_USED':
        return 'This invitation has already been used. If you\'ve already created an account, try logging in.';
      case 'CANCELLED':
        return `This invitation has been cancelled. Please contact ${contactText} if you need access.`;
      default:
        return 'Invalid or expired invitation link. Please check your email for the correct link.';
    }
  };

  // Get invitation details from validation result
  const invitation = validationResult && ('schoolName' in validationResult || 'familyName' in validationResult) ? validationResult : null;

  // Build school-scoped login URL so the user lands on the correct school's login page
  const schoolSlug = invitation && 'schoolSlug' in invitation ? String(invitation.schoolSlug) : null;
  const loginUrl = schoolSlug ? `/${schoolSlug}/login` : '/login';
  const legal = usePublicLegal(schoolSlug, needsAgreement);

  // Build invitation description text
  const getInvitationText = () => {
    if (!invitation) return null;

    const schoolName = 'schoolName' in invitation ? String(invitation.schoolName) : 'the music school';

    if (isStudentInvitation) {
      return (
        <>
          You've been invited to create a student account at {schoolName}. Set up your password to get started!
        </>
      );
    } else {
      const familyName = 'familyName' in invitation ? String(invitation.familyName) : 'your family';
      return (
        <>
          You've been invited to join <strong>{familyName}</strong> on the {schoolName} parent portal.
        </>
      );
    }
  };

  // Resolve the page content based on state
  const renderContent = () => {
    // Loading state
    if (isValidating) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Validating invitation...
          </Typography>
        </Paper>
      );
    }

    // Token error state
    if (isTokenInvalid) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Invitation Not Valid
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {getTokenErrorMessage()}
          </Alert>
          <Button variant="contained" onClick={() => navigate(loginUrl)}>
            Go to Login
          </Button>
        </Paper>
      );
    }

    // Success state
    if (acceptInvitation.isSuccess) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
              color: 'primary.main',
              mb: 2,
            }}
          >
            Account Created!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your account has been created successfully. You can now log in with your email address.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(loginUrl)}
          >
            Go to Login
          </Button>
        </Paper>
      );
    }

    // Main form
    return (
      <Paper sx={{ p: 4 }}>
        {invitation && (
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            {getInvitationText()}
          </Typography>
        )}

        {acceptInvitation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(acceptInvitation.error as Error & { response?: { data?: { message?: string } } })?.response?.data?.message ||
              'Failed to create account. Please try again.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Create Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={acceptInvitation.isPending}
            error={submitted && !isPasswordValid}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {password && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                color={getStrengthColor(passwordStrength)}
                sx={{ height: 6, borderRadius: 3, mb: 1 }}
              />
              <List dense disablePadding>
                <PasswordRequirement met={passwordValidation.minLength} label="At least 8 characters" />
                <PasswordRequirement met={passwordValidation.hasUppercase} label="One uppercase letter" />
                <PasswordRequirement met={passwordValidation.hasLowercase} label="One lowercase letter" />
                <PasswordRequirement met={passwordValidation.hasNumber} label="One number" />
                <PasswordRequirement met={passwordValidation.hasSpecial} label="One special character" />
              </List>
            </Box>
          )}

          <TextField
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={acceptInvitation.isPending}
            error={submitted && !passwordsMatch}
            helperText={submitted && !passwordsMatch ? 'Passwords do not match' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {needsAgreement && (
            <Box sx={{ mt: 3 }}>
              {(
                [
                  {
                    key: 'terms',
                    title: 'Terms & Conditions',
                    text: legal.data?.termsOfService ?? null,
                    checked: termsChecked,
                    set: setTermsChecked,
                  },
                  {
                    key: 'privacy',
                    title: 'Privacy Policy',
                    text: legal.data?.privacyPolicy ?? null,
                    checked: privacyChecked,
                    set: setPrivacyChecked,
                  },
                ] as const
              ).map((doc) => (
                <Box key={doc.key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {doc.title}
                  </Typography>
                  {legal.isLoading ? (
                    <CircularProgress size={20} aria-label={`Loading the ${doc.title}`} />
                  ) : doc.text ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                        maxHeight: 160,
                        overflowY: 'auto',
                        fontSize: '0.8125rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {doc.text}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Please contact the school for a copy of their {doc.title}.
                    </Typography>
                  )}
                  {schoolSlug && (
                    <Link
                      href={`/${schoolSlug}/legal/${doc.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      Open in a new tab
                    </Link>
                  )}
                  <FormControlLabel
                    sx={{ display: 'flex', mt: 0.5 }}
                    control={
                      <Checkbox
                        checked={doc.checked}
                        onChange={(e) => doc.set(e.target.checked)}
                        disabled={acceptInvitation.isPending}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I have read and agree to the {doc.title}. <strong>(Required)</strong>
                      </Typography>
                    }
                  />
                </Box>
              ))}
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 3 }}
            disabled={acceptInvitation.isPending || !isPasswordValid || !passwordsMatch || !agreed}
          >
            {acceptInvitation.isPending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          Already have an account?{' '}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate(loginUrl)}
            sx={{ textTransform: 'none' }}
          >
            Log in
          </Button>
        </Typography>
      </Paper>
    );
  };

  // Page title
  const inviteeFirstName = invitation && 'inviteeFirstName' in invitation ? String(invitation.inviteeFirstName) : null;
  const pageTitle = isStudentInvitation
    ? `Welcome${inviteeFirstName ? `, ${inviteeFirstName}` : ''}!`
    : 'Welcome!';

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character Decorations - Left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character Decorations - Right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music Notes Decorations */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile Header - Logo centered */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 120,
            height: 'auto',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          {pageTitle}
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {pageTitle}
        </Typography>
      </Box>

      {/* Subtitle - only for non-student invitations */}
      {!isStudentInvitation && (
        <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            Set up your password to get started
          </Typography>
        </Container>
      )}

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        {renderContent()}
      </Container>

      <PublicFooter />
    </Box>
  );
}

// ===========================================
// HELPER COMPONENTS
// ===========================================

interface PasswordRequirementProps {
  met: boolean;
  label: string;
}

function PasswordRequirement({ met, label }: PasswordRequirementProps) {
  return (
    <ListItem sx={{ py: 0.25 }}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        {met ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <CloseIcon fontSize="small" color="error" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          variant: 'body2',
          color: met ? 'success.main' : 'text.secondary',
        }}
      />
    </ListItem>
  );
}
